import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import PdfViewerModal from '../Modals/PdfViewerModal';
import PatientDetailImageModal from '../Modals/PatientDetailImageModal'; // Import the image viewer modal
import { RiCloseLine } from 'react-icons/ri';
import BaseImage from '../BaseImage';
import PdfIcon from '../../assets/images/reports-icons/pdf_attachment_icon.webp';
import PngIcon from '../../assets/images/reports-icons/png_attachment_icon.webp';
import JpgIcon from '../../assets/images/reports-icons/jpg_attachment_icon.webp';
import DownloadIcon from '../../assets/images/reports-icons/pdf-modal-download-icon.svg';

const getFileIcon = (fileType) => {
  switch (fileType) {
    case 'application/pdf':
      return PdfIcon;
    case 'image/png':
      return PngIcon;
    case 'image/jpeg':
    case 'image/jpg':
      return JpgIcon;
    default:
      return PdfIcon; // Default to PDF icon if the type is unrecognized
  }
};

const AttachedFile = ({ file, onRemove, onDownload, isLocal = true }) => {
  const fileSizeKB = (file.size / 1024).toFixed(1);
  const fileType = file.mimeType || file.type || ''; // Safeguard to ensure file type is always defined
  const fileIcon = getFileIcon(fileType); // Use the correct file type
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isImageModalOpen, setImageModalOpen] = useState(false);

  const handleFileClick = (file) => {
    if (fileType === 'application/pdf') {
      setSelectedFile(file);
      setModalOpen(true);
    } else if (fileType.startsWith('image/')) {
      // Handle image files
      setSelectedFile(file);
      setImageModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedFile(null);
  };

  const handleImageModalClose = () => {
    setImageModalOpen(false);
    setSelectedFile(null);
  };

  const handleFileDownload = (e) => {
    e.stopPropagation();
    const link = document.createElement('a');
    if (file.url) {
      // If it's a remote file, use the URL
      link.href = file.url;
    } else {
      // If it's a local file, create an object URL
      link.href = URL.createObjectURL(file);
    }
    link.download = file.filename || file.name; // Use filename for remote and name for local
    link.click();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#ECEFF1',
          borderRadius: '8px',
          padding: '4px',
          marginRight: '8px',
          maxWidth: '200px',
          gap: '10px',
          cursor: 'pointer'
        }}
        onClick={() => handleFileClick(file)}
      >
        <BaseImage src={fileIcon} sx={{ width: 'auto', flexShrink: 0 }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="subText"
            color="primary.mainBlue"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: 'pointer',
            }}
            title={file.filename || file.name} // Use filename for remote files
          >
            {file.filename || file.name}
          </Typography>
          <Typography variant="subText" color="primary.mainBlue">
            {fileType ? fileType.split('/')[1]?.toUpperCase() : 'UNKNOWN'} - {fileSizeKB}KB
          </Typography>
        </Box>
        {onRemove && (
          <IconButton
            onClick={onRemove}
            size="small"
            sx={{
              color: '#000',
            }}
          >
            <RiCloseLine size={20} />
          </IconButton>
        )}
        {onDownload && (
          <IconButton
            onClick={e => handleFileDownload(e)}
            size="small"
            sx={{
              color: '#000',
              display: 'flex',
            }}
          >
            <BaseImage
              sx={{
                width: '12px',
                height: '12px',
                display: 'flex'
              }}
              sximage={{display: 'flex'}}
              src={DownloadIcon}
              alt="icon-download"
            />
          </IconButton>
        )}
      </Box>
      {selectedFile && fileType === 'application/pdf' && (
          <PdfViewerModal
            open={isModalOpen}
            onClose={handleModalClose}
            file={file.url ? file.url : URL.createObjectURL(selectedFile)} // Handle both remote and local
            fileName={file.filename || file.name}
          />
        )}
        {selectedFile && fileType.startsWith('image/') && (
          <PatientDetailImageModal
            open={isImageModalOpen}
            onClose={handleImageModalClose}
            imageUrl={file.url ? file.url : URL.createObjectURL(selectedFile)} // Handle both remote and local
            altText={file.filename || file.name}
            fileName={file.filename || file.name}
          />
        )}
    </>
  );
};

export default AttachedFile;
